import { createAsyncThunk } from "@reduxjs/toolkit";

import { getWriteReviewTemplate } from "../../../api/reviews";
import {
    IFormUUID,
    IReviewsProductID,
    IReviewsProductTypeID,
} from "../../../models/nominals";
import { IReviewsProduct, IWriteReviewTemplate } from "../../../models/reviews";

/**
 * Load a review template for a specific product.
 *
 * This thunk fetches the review template structure from the API for a given product.
 * The template defines what fields should be shown in the write review form (e.g., rating,
 * headline, comments) and their validation requirements.
 *
 * @param {Object} params - The parameters object
 * @param {IFormUUID} params.formUUID - The unique identifier for the form instance
 * @param {IReviewsProductID} params.productID - The product to load the template for
 *
 * @returns {Promise} Promise resolving to an object containing:
 *   - formUUID: The form identifier (passed through for reducer)
 *   - productID: The product identifier (passed through for reducer)
 *   - template: The review template structure from the API
 *
 * @example
 * // Load template when user selects a product from dropdown
 * dispatch(loadReviewTemplate({
 *   formUUID: 'form-123',
 *   productID: 456
 * }));
 *
 * @sideEffects
 * - Updates `ui.writeReviewForms[formUUID].selectedProductID` via extraReducers
 * - Updates `data.writeReviewTemplates` with the fetched template via extraReducers
 */
export const loadReviewTemplate = createAsyncThunk(
    "reviews/loadReviewTemplate",
    async ({
        formUUID,
        productID,
    }: {
        formUUID: IFormUUID;
        productID: IReviewsProductID;
    }): Promise<{
        formUUID: IFormUUID;
        productID: IReviewsProductID;
        template: IWriteReviewTemplate;
    }> => {
        const template = await getWriteReviewTemplate(productID);
        return { formUUID, productID, template };
    },
);

/**
 * Load a review template for the first product of a given product type.
 *
 * This thunk is used when the user selects a product type (e.g., "Mattresses") but hasn't
 * selected a specific product yet. It finds the first product of that type and loads its
 * review template. This is commonly used in multi-product review forms where users first
 * select a category, then a specific product.
 *
 * @param {Object} params - The parameters object
 * @param {IFormUUID} params.formUUID - The unique identifier for the form instance
 * @param {IReviewsProduct[]} params.products - Array of all available products
 * @param {IReviewsProductTypeID} params.productTypeID - The product type to filter by
 *
 * @returns {Promise} Promise resolving to an object containing:
 *   - formUUID: The form identifier (passed through for reducer)
 *   - productID: The ID of the first product of the selected type
 *   - template: The review template structure from the API
 *   Returns null if no products match the given type.
 *
 * @example
 * // Load template when user selects product type in multi-product form
 * dispatch(loadReviewTemplateByProductType({
 *   formUUID: 'form-123',
 *   products: allProducts,
 *   productTypeID: 'mattress'
 * }));
 *
 * @sideEffects
 * - Updates `ui.writeReviewForms[formUUID].selectedProductID` via extraReducers (if product found)
 * - Updates `data.writeReviewTemplates` with the fetched template via extraReducers (if product found)
 */
export const loadReviewTemplateByProductType = createAsyncThunk(
    "reviews/loadReviewTemplateByProductType",
    async ({
        formUUID,
        products,
        productTypeID,
    }: {
        formUUID: IFormUUID;
        products: ReadonlyArray<IReviewsProduct>;
        productTypeID: IReviewsProductTypeID;
    }): Promise<{
        formUUID: IFormUUID;
        productID: IReviewsProductID;
        template: IWriteReviewTemplate;
    } | null> => {
        // Find products of the selected type
        const typeProducts = products.filter(
            (product) => product.product_type === productTypeID,
        );

        // Get the first product of this type
        const initialProduct = typeProducts[0];
        if (!initialProduct) {
            return null;
        }

        // Load the review template for this product
        const template = await getWriteReviewTemplate(initialProduct.id);
        return {
            formUUID,
            productID: initialProduct.id,
            template,
        };
    },
);
